"use client";

import { CheckCircleIcon, InfoIcon, XCircleIcon, type Icon } from "@phosphor-icons/react";

import { type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { cn } from "@/src/lib/shadcn-utils";
import { TaskStepPhase } from "@/src/models/task-step-card";

/**
 * How each step phase presents: the badge ops reads in the card header, the icon beside it, and
 * the `ValerieCard` variant that colours the header band.
 *
 * The icons are the ones the rest of the app already uses for the equivalent task-level status in
 * `src/components/tasks/model-icons.tsx` — `InfoIcon` for review/verification needed,
 * `CheckCircleIcon` for completed, `XCircleIcon` for failed — so a phase reads the same on a step
 * card as it does in the task list. Review needed and Verify share `InfoIcon` there too; the
 * colour and the label tell them apart.
 *
 * Shared with the "What needs your attention" summary so a phase can never be labelled or
 * coloured one way at the top of the page and another way on the card itself.
 */
export const TASK_STEP_PHASE_PRESENTATION: Record<
  TaskStepPhase,
  { label: string; icon: Icon; variant: ValerieCardHeaderVariant; chipClassName: string }
> = {
  /**
   * Light green, and most of a healthy referral. It deepens to `verified` once ops signs off —
   * same hue family, so signing off reads as the card getting stronger rather than changing its
   * subject.
   */
  [TaskStepPhase.QA]: {
    label: "QA",
    icon: CheckCircleIcon,
    variant: "qa",
    chipClassName: "border-qa bg-qa text-qa-foreground",
  },
  [TaskStepPhase.ACTION_NEEDED]: {
    label: "Action needed",
    icon: InfoIcon,
    variant: "caution",
    chipClassName: "border-caution bg-caution text-caution-foreground",
  },
  [TaskStepPhase.FAILED]: {
    label: "Failed",
    icon: XCircleIcon,
    variant: "failed",
    chipClassName: "border-failed bg-failed text-failed-foreground",
  },
};

/** Order ops should work the page in: what's broken first, what's a formality last. */
export const TASK_STEP_PHASE_ORDER: TaskStepPhase[] = [
  TaskStepPhase.FAILED,
  TaskStepPhase.ACTION_NEEDED,
  TaskStepPhase.QA,
];

/**
 * Phase name plus its icon, for the summary card's count chips.
 *
 * Drawn in the phase's own colours rather than `currentColor`, because here the chips sit side by
 * side on a neutral surface and the colour is the only thing distinguishing them. On a card header
 * the band already carries the colour, so the header badge inherits instead — see `TaskStepCard`.
 */
export function TaskStepPhaseChip({
  phase,
  count,
  className,
}: {
  phase: TaskStepPhase;
  count?: number;
  className?: string;
}) {
  const { label, icon: PhaseIcon, chipClassName } = TASK_STEP_PHASE_PRESENTATION[phase];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
        chipClassName,
        className
      )}
    >
      <PhaseIcon weight="fill" className="size-4 shrink-0" />
      {count === undefined ? label : `${count} ${label}`}
    </span>
  );
}
